import { settled } from "@scripts/settle";

export const EBar = { INITIAL: "initial", SCROLLED: "scrolled", COLLAPSED: "collapsed" } as const;
export type EBar = (typeof EBar)[keyof typeof EBar];

export const EPanel = {
	MEGA: "mega",
	UTILITY: "utility",
	DRAWER: "drawer",
	BRANDS: "brands",
	ACCOUNT: "account",
} as const;
export type EPanel = (typeof EPanel)[keyof typeof EPanel];

export type HeaderState = {
	bar: EBar;
	panel: EPanel | null;
};

export type HeaderStore = {
	read: () => HeaderState;
	setBar: (bar: EBar) => void;
	setPanel: (panel: EPanel | null) => void;
	subscribe: (listener: () => void) => () => void;
	/** The element whose own transition ends when this panel has finished leaving. */
	register: (panel: EPanel, element: HTMLElement) => void;
	elementFor: (panel: EPanel) => HTMLElement | undefined;
	/** `createPanel` reports its own page hold here, so nothing has to keep a second list of
	    which panels inert the page. The account dropdown is the one that never holds. */
	holdPage: (panel: EPanel, held: boolean) => void;
	pageHeld: () => boolean;
};

const BAR = "fa-header--";
const PANEL = "fa-header--panel-";
const OPEN = "fa-header--panel-open";

const BARS = Object.values(EBar).map((bar) => `${BAR}${bar}`);
const PANELS = Object.values(EPanel).map((panel) => `${PANEL}${panel}`);

const START: HeaderState = { bar: EBar.INITIAL, panel: null };

const create = (root: HTMLElement): HeaderStore => {
	let state: HeaderState = START;
	const listeners = new Set<() => void>();
	const elements = new Map<EPanel, HTMLElement>();
	const holding = new Set<EPanel>();

	const paint = () => {
		root.classList.remove(...BARS, ...PANELS);
		root.classList.add(`${BAR}${state.bar}`);

		if (state.panel !== null) root.classList.add(`${PANEL}${state.panel}`);

		root.classList.toggle(OPEN, state.panel !== null);
	};

	/* Copied before iterating: a listener closing its own panel sets the store again. */
	const notify = () => {
		for (const listener of [...listeners]) listener();
	};

	const set = (next: Partial<HeaderState>) => {
		const merged = { ...state, ...next };

		if (merged.bar === state.bar && merged.panel === state.panel) return;

		state = merged;
		paint();
		notify();
	};

	paint();

	return {
		read: () => state,
		setBar: (bar) => {
			set({ bar });
		},
		setPanel: (panel) => {
			set({ panel });
		},
		subscribe: (listener) => {
			listeners.add(listener);

			return () => {
				listeners.delete(listener);
			};
		},
		register: (panel, element) => {
			elements.set(panel, element);
		},
		elementFor: (panel) => elements.get(panel),
		/* Notifies on the flip, because a hand-over gives the field up before the outgoing panel
		   gives the page back — a follower reading the field alone sees the page as still held
		   for the whole of the 200ms race. */
		holdPage: (panel, held) => {
			const was = holding.size > 0;

			if (held) holding.add(panel);
			else holding.delete(panel);

			if (was !== holding.size > 0) notify();
		},
		pageHeld: () => holding.size > 0,
	};
};

/* Only if `transitionend` never arrives — an interrupted transition, or a browser that drops the
   event. Reduced motion is handled before this, because there the transition is removed outright. */
const SETTLE_FALLBACK = 1000;

/* The incoming panel starts here at the latest, so a long exit plays out underneath it rather
   than in front of it. Waiting for the whole exit reads as a stall, not as a hand-over. */
const HANDOVER = 200;

const after = (ms: number): Promise<void> =>
	new Promise((resolve) => {
		window.setTimeout(resolve, ms);
	});

type Take = {
	/* Runs once the field is clear, never before: `followPanel` reads the caller's own `isOpen`,
	   so a panel that marks itself open first is dismissed by its own follower. */
	claim: () => void;
	stillWanted: () => boolean;
};

/** Takes the field, waiting first for whatever holds it to finish leaving. `stillWanted` is
    re-read after the wait, so a press that cancels mid-exit does not open the panel behind it. */
export const takePanel = async (
	store: HeaderStore,
	panel: EPanel,
	{ claim, stillWanted }: Take,
): Promise<void> => {
	const busy = store.read().panel;

	if (busy === null || busy === panel) {
		claim();
		store.setPanel(panel);
		return;
	}

	store.setPanel(null);
	claim();

	const outgoing = store.elementFor(busy);
	const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

	if (outgoing !== undefined && !reduced)
		await Promise.race([settled(outgoing, SETTLE_FALLBACK), after(HANDOVER)]);

	if (stillWanted()) store.setPanel(panel);
};

/** Clears the field only while it still names this panel — a peer opening has already claimed
    it, and clearing then would take the panel that just opened down with it. */
export const releasePanel = (store: HeaderStore, panel: EPanel): void => {
	if (store.read().panel === panel) store.setPanel(null);
};

/** Dismisses this panel whenever the field names another. Returns its own unsubscribe. */
export const followPanel = (
	store: HeaderStore,
	panel: EPanel,
	isOpen: () => boolean,
	dismiss: () => void,
): (() => void) =>
	store.subscribe(() => {
		if (store.read().panel !== panel && isOpen()) dismiss();
	});

const stores = new WeakMap<HTMLElement, HeaderStore>();

/** Keyed by root, so two headers on one page hold their own state. */
export const getHeaderStore = (root: HTMLElement): HeaderStore => {
	let store = stores.get(root);

	if (!store) {
		store = create(root);
		stores.set(root, store);
	}

	return store;
};

export const dropHeaderStore = (root: HTMLElement): void => {
	stores.delete(root);
	root.classList.remove(...BARS, ...PANELS, OPEN);
};
